import {
  ApplicantStatus,
  ApplicantType,
  ApplicationFeeStatus,
  ApplicationStatus,
  Invitee
} from "shared/types";
import { Applicant } from "types/application";
import { ActionState } from "shared/utils/application-actions";
import { ApplicationActionsMenuVariant } from "./types";

// ---------------------------------------------------------------------------
// ActionState factories (mirrored from application-actions)
// ---------------------------------------------------------------------------

const hidden = (): ActionState => ({
  disabled: true,
  hidden: true,
  tooltip: undefined
});

const disabledWith = (tooltip: string): ActionState => ({
  disabled: true,
  hidden: false,
  tooltip
});

const enabled: ActionState = {
  disabled: false,
  hidden: false,
  tooltip: undefined
};

// ---------------------------------------------------------------------------
// Constructor options
// ---------------------------------------------------------------------------

export const isApplicantEntity = (
  applicant: Applicant | Invitee
): applicant is Applicant => {
  return "verifications" in applicant && Array.isArray(applicant.verifications);
};

interface ApplicantActionsOptions {
  variant: ApplicationActionsMenuVariant;
  applicant: Applicant | Invitee;
  pendingMagicLink?: Invitee;
  applicationStatus?: ApplicationStatus;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  hasAdminPrivileges: boolean;
  permissions: {
    canChangeApplicantRole?: boolean;
    canCancelApplications?: boolean;
  };
}

// ---------------------------------------------------------------------------
// ApplicantActions
// ---------------------------------------------------------------------------

export class ApplicantActions {
  private variant: ApplicationActionsMenuVariant;
  private applicant: Applicant | Invitee;
  private pendingMagicLink?: Invitee;
  private applicationStatus?: ApplicationStatus;
  private allowCoSigner: boolean;
  private allowOccupant: boolean;
  private hasAdminPrivileges: boolean;
  private permissions: ApplicantActionsOptions["permissions"];

  constructor(opts: ApplicantActionsOptions) {
    this.variant = opts.variant;
    this.applicant = opts.applicant;
    this.pendingMagicLink = opts.pendingMagicLink;
    this.applicationStatus = opts.applicationStatus;
    this.allowCoSigner = opts.allowCoSigner;
    this.allowOccupant = opts.allowOccupant;
    this.hasAdminPrivileges = opts.hasAdminPrivileges;
    this.permissions = opts.permissions;
  }

  private get isApplicant() {
    return isApplicantEntity(this.applicant);
  }

  private get applicantStatus(): ApplicantStatus | undefined {
    return this.applicant.application_status as ApplicantStatus | undefined;
  }

  private get isApplicationFinalized() {
    return Boolean(
      this.applicationStatus !== undefined &&
      [
        ApplicationStatus.Canceled,
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved
      ].includes(this.applicationStatus)
    );
  }

  private get isApplicationDecisioned() {
    return Boolean(
      this.applicationStatus !== undefined &&
      [
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved
      ].includes(this.applicationStatus)
    );
  }

  get review(): ActionState {
    if (this.variant !== ApplicationActionsMenuVariant.Group) return hidden();

    if (!this.isApplicant)
      return disabledWith("Invited applications don't have any data to review");

    return enabled;
  }

  get resendInvite(): ActionState {
    const isActive = Boolean(
      this.pendingMagicLink?.active &&
      !this.pendingMagicLink.used &&
      this.applicationStatus !== undefined &&
      ![
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved,
        ApplicationStatus.Canceled,
        ApplicationStatus.OnHold
      ].includes(this.applicationStatus)
    );

    if (!isActive) return hidden();

    return enabled;
  }

  get changeRole(): ActionState {
    if (!this.isApplicant) return hidden();

    const hasPermission =
      this.hasAdminPrivileges || this.permissions.canChangeApplicantRole;
    const hasRoleOptions = this.allowCoSigner || this.allowOccupant;
    const isSubmittedOrCanceled =
      this.applicantStatus !== undefined &&
      [ApplicantStatus.Submitted, ApplicantStatus.Canceled].includes(
        this.applicantStatus
      );

    const canChange = Boolean(
      hasPermission &&
      hasRoleOptions &&
      isApplicantEntity(this.applicant) &&
      (this.applicant as Applicant).first_name &&
      isSubmittedOrCanceled
    );

    if (!canChange) {
      if (!hasPermission)
        return disabledWith(
          "You don't have permission to change roles. Please contact your administrator"
        );
      if (!hasRoleOptions)
        return disabledWith(
          "Applicant is the only role available in this template"
        );
      if (this.applicantStatus === ApplicantStatus.Canceled)
        return disabledWith("You cannot change role for cancelled applicant");
      return disabledWith(
        "You can't change role until application is submitted"
      );
    }

    return enabled;
  }

  get setPrimaryApplicant(): ActionState {
    if (!this.isApplicant) return hidden();

    const isPrimary = Boolean(
      isApplicantEntity(this.applicant) && this.applicant.is_primary
    );
    const applicantRole = this.applicant.role as ApplicantType;
    const isPermitted =
      this.hasAdminPrivileges ||
      Boolean(this.permissions.canChangeApplicantRole);

    if (!isPermitted)
      return disabledWith(
        "You don't have permission to change the primary applicant"
      );

    if (applicantRole !== ApplicantType.Applicant)
      return disabledWith(
        "You can only set the primary applicant for an applicant"
      );

    if (isPrimary)
      return disabledWith("This applicant is already the primary applicant");

    return enabled;
  }

  get cancelOrUncancel(): ActionState & { showUncancel: boolean } {
    const isCanceledApplication =
      this.applicationStatus === ApplicationStatus.Canceled ||
      this.applicantStatus === ApplicantStatus.Canceled;

    const hasPermission =
      this.hasAdminPrivileges || this.permissions.canCancelApplications;

    const showUncancel = Boolean(hasPermission && isCanceledApplication);

    if (showUncancel) {
      const isOnHold = this.applicationStatus === ApplicationStatus.OnHold;
      const isDisabled = isOnHold || this.isApplicationDecisioned;
      const tooltip = this.isApplicationDecisioned
        ? "Application has already been decisioned. To uncancel applicant, you'll first need to undo decision for the application."
        : "You need to resume the application before uncanceling";

      return {
        showUncancel: true,
        disabled: isDisabled,
        hidden: false,
        tooltip: isDisabled ? tooltip : undefined
      };
    }

    const isCancelApplicantDisabled =
      this.isApplicationFinalized ||
      this.applicantStatus === ApplicantStatus.Canceled;

    let cancelTooltip: string | undefined;
    if (isCancelApplicantDisabled)
      cancelTooltip = isCanceledApplication
        ? "Can not cancel a canceled application"
        : "You can't cancel application once the decisions have been made";

    return {
      showUncancel: false,
      disabled: isCancelApplicantDisabled,
      hidden: false,
      tooltip: cancelTooltip
    };
  }

  get printReceipt(): ActionState {
    if (!this.hasAdminPrivileges) return hidden();

    const applicant = this.applicant;
    const canPrint = Boolean(
      this.isApplicant &&
      isApplicantEntity(applicant) &&
      applicant.application_submitted_at &&
      applicant.application_fee_status !== ApplicationFeeStatus.Refunded &&
      applicant.paid_application_fee &&
      (applicant.paid_application_fee.cents || 0) > 0
    );

    if (!canPrint) {
      if (
        !this.isApplicant ||
        !isApplicantEntity(applicant) ||
        !applicant.application_submitted_at
      )
        return disabledWith(
          "Please wait until applicant submits their application"
        );

      if (
        "fee_statuses" in applicant &&
        applicant.fee_statuses?.some((fs) => fs.status === "refunded")
      )
        return disabledWith(
          "Receipts are not available for applicants with refunded fees"
        );

      return disabledWith("Receipt can only be printed for paid applicants");
    }

    return enabled;
  }

  get refund(): ActionState {
    if (!this.hasAdminPrivileges) return hidden();

    const applicant = this.applicant;
    const isRefundable = Boolean(
      this.isApplicant &&
      isApplicantEntity(applicant) &&
      applicant.paid_application_fee != null &&
      applicant.application_status === ApplicantStatus.Submitted &&
      applicant.application_fee_status === ApplicationFeeStatus.Paid &&
      (applicant.paid_application_fee.cents || 0) > 0
    );

    if (!isRefundable) {
      const displayRole =
        applicant.role === ApplicantType.CoSigner
          ? "guarantor"
          : applicant.role;

      if (applicant.application_status === ApplicantStatus.Canceled)
        return disabledWith(
          "You can't request a refund for a canceled application"
        );

      if (
        isApplicantEntity(applicant) &&
        applicant.application_fee_status === ApplicationFeeStatus.Refunded
      )
        return disabledWith(
          "Refund has already been requested for this application"
        );

      return disabledWith(
        `You can request a refund if the ${displayRole} has paid the application fee`
      );
    }

    return enabled;
  }

  get uploadDocuments(): ActionState {
    if (!this.hasAdminPrivileges) return hidden();

    if (!this.isApplicant)
      return disabledWith(
        "Applicants need to start their application before documents can be uploaded"
      );

    return enabled;
  }
}
